import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { CheckMark } from '../components/Menu';
import { firstEnabledIndex, isRovingKey, resolveRovingIndex } from '../components/rovingFocus';
import { usePopoverClamp } from '../components/usePopoverClamp';
import { usePopoverDismiss } from '../components/usePopoverDismiss';

/**
 * MENU DE DÉBORDEMENT DES BASCULES D'AFFICHAGE de la visionneuse (mobile uniquement).
 *
 * POURQUOI — à 375 px, la barre du haut était DÉJÀ pleine avec le compteur et six
 * boutons. Les trois actions d'écriture (pivoter ×2, poubelle) n'y tenaient pas. Ce qui
 * cède est ce qui n'engage rien : les quatre bascules d'AFFICHAGE (note, commentaires,
 * légende, détail) se replient ici, tandis que les actions et « Fermer » restent des
 * cibles directes. Dès `sm`, ce menu disparaît (`sm:hidden`) et les quatre boutons
 * reprennent leur place — le desktop ne change pas d'un pixel.
 *
 * POURQUOI PAS `ToolsMenu`/`OverflowMenu`/`Menu` — les deux premiers portent des ACTIONS
 * (`ToolAction` n'a aucun état coché), le troisième une sélection UNIQUE
 * (`menuitemradio` : cocher l'un décoche les autres). Il s'agit ici de quatre bascules
 * INDÉPENDANTES, donc de `menuitemcheckbox` + `aria-checked` (+ coche visible : jamais
 * l'information par la couleur seule). Seule la présentation est neuve — la fermeture
 * (clic extérieur, retour du focus) vient de `usePopoverDismiss` et la navigation par
 * flèches de `rovingFocus`, les deux noyaux uniques du projet.
 *
 * ÉCHAP — la visionneuse écoute `Échap` sur `window` pour se FERMER. Sans précaution,
 * refermer ce menu au clavier refermerait aussi la visionneuse derrière lui. Le
 * gestionnaire ci-dessous arrête donc la propagation de l'événement natif (React relaie
 * `stopPropagation` au natif, et sa racine est en deçà de `window`) : tant que le menu
 * est ouvert, `Échap` ne referme QUE lui.
 */

/** Une bascule d'affichage : nom neutre de ce qu'elle montre, état, et son inverseur. */
export interface DisplayToggle {
  /** Clé stable de l'entrée (React `key`). */
  id: string;
  /** Nom de ce qui est affiché (« Détail »…), PAS l'action — l'état est porté par la coche. */
  label: string;
  checked: boolean;
  onToggle: () => void;
}

interface ViewerDisplayMenuProps {
  /** Libellé accessible du déclencheur et du menu. */
  label: string;
  /** Rappel `sr-only` de la fermeture au clavier (`a11y.closeMenu`). */
  closeLabel: string;
  toggles: readonly DisplayToggle[];
}

export function ViewerDisplayMenu({
  label,
  closeLabel,
  toggles,
}: ViewerDisplayMenuProps): React.JSX.Element {
  const { open, rootRef, triggerRef, openPopover, close } = usePopoverDismiss<
    HTMLDivElement,
    HTMLButtonElement
  >();
  const [activeIndex, setActiveIndex] = useState(0);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  // Maintien dans le cadre visible. Ce déclencheur n'est pas le dernier de la barre de
  // la visionneuse (« Fermer » le suit) : son panneau `min-w-52` déborde donc lui aussi.
  const panelRef = usePopoverClamp(open);

  /** Aucune bascule n'est jamais désactivée — le noyau commun exige malgré tout le masque. */
  const enabled = useMemo(() => toggles.map(() => true), [toggles]);

  const openMenu = useCallback(() => {
    setActiveIndex(firstEnabledIndex(enabled));
    openPopover();
  }, [enabled, openPopover]);

  // Focus de l'entrée active à l'ouverture, puis à chaque déplacement par flèches.
  useEffect(() => {
    if (open) itemsRef.current[activeIndex]?.focus();
  }, [open, activeIndex]);

  const onTriggerKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  };

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation(); // cf. l'en-tête : la visionneuse ferme sur `Échap`.
      close();
      return;
    }
    if (e.key === 'Tab') {
      close(false);
      return;
    }
    if (!isRovingKey(e.key, 'vertical')) return;
    e.preventDefault();
    const next = resolveRovingIndex(e.key, activeIndex, enabled, 'vertical');
    if (next !== null) setActiveIndex(next);
  };

  return (
    <div ref={rootRef} className="relative sm:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        // `p-2` (40 px) et non `p-2.5` : ce déclencheur n'existe QUE sous `sm`, où toute
        // la barre est à 40 px pour tenir dans 375 px. Un bouton plus grand que ses
        // voisins immédiats se verrait, et ne servirait à rien.
        className="rounded-md p-2 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
      >
        <EllipsisHorizontalIcon className="h-6 w-6" aria-hidden="true" />
      </button>
      {open && (
        // Panneau à la couleur des SURFACES de l'application (comme tous les autres
        // menus) et non au sombre de la barre : il flotte au-dessus de la photo, où un
        // fond translucide serait illisible.
        <div
          ref={panelRef}
          role="menu"
          id={menuId}
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className="absolute end-0 z-30 mt-1 min-w-52 rounded-lg border border-border bg-surface p-1 text-fg shadow-lg"
        >
          {toggles.map((toggle, index) => (
            <button
              key={toggle.id}
              ref={(el) => {
                itemsRef.current[index] = el;
              }}
              type="button"
              role="menuitemcheckbox"
              aria-checked={toggle.checked}
              tabIndex={index === activeIndex ? 0 : -1}
              onClick={toggle.onToggle}
              className="flex min-h-11 w-full items-center gap-2 rounded-md px-3 py-2 text-start text-sm text-fg transition-colors hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand aria-checked:font-semibold aria-checked:text-brand motion-reduce:transition-none"
            >
              <span className="flex-1">{toggle.label}</span>
              {toggle.checked && <CheckMark />}
            </button>
          ))}
          <span className="sr-only">{closeLabel}</span>
        </div>
      )}
    </div>
  );
}
